// AudioWaveform.tsx
import React, { useRef, useEffect, useState } from "react";
import { isMobile } from "react-device-detect";
import { useTheme } from "@mui/material/styles";

interface AudioWaveformProps {
  mediaStream: MediaStream | null; // Pass the MediaStream from the recording component
}

const AudioWaveform: React.FC<AudioWaveformProps> = ({ mediaStream }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  // Use the theme to apply consistent colors and styles
  const theme = useTheme();

  // Bar heights (initial heights mimic the logo)
  const [initialHeights] = useState([0.7, 0.9, 0.6, 0.8, 0.5, 0.7]);

  useEffect(() => {
    if (!mediaStream) return;

    const audioContext = new AudioContext();
    const analyser = audioContext.createAnalyser();

    // Set FFT size to 256 to get fewer frequency bins
    analyser.fftSize = 1024;

    const source = audioContext.createMediaStreamSource(mediaStream);
    source.connect(analyser);

    audioContextRef.current = audioContext;
    analyserRef.current = analyser;

    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const draw = () => {
      const canvas = canvasRef.current;
      if (!canvas || !analyserRef.current) return;

      const canvasCtx = canvas.getContext("2d");
      if (!canvasCtx) return;

      analyserRef.current.getByteFrequencyData(dataArray);

      // Clear the canvas
      canvasCtx.clearRect(0, 0, canvas.width, canvas.height);

      // Set the number of bars to 6, matching the design
      const numberOfBars = 6 * 3;
      const barWidth = canvas.width / numberOfBars;
      let x = 0;

      // Calculate the number of frequency bins per bar
      const binsPerBar = Math.floor(bufferLength / numberOfBars);

      for (let i = 0; i < numberOfBars; i++) {
        let sum = 0;
        for (let j = 0; j < binsPerBar; j++) {
          sum += dataArray[i * binsPerBar + j];
        }
        const avg = sum / binsPerBar;

        // Make the bars more reactive by increasing the influence of normalizedHeight
        const normalizedHeight = (avg / 255) * canvas.height * 0.9;

        // Initial bar heights
        const initialHeight = initialHeights[i] * canvas.height;

        // Increase the weight of normalizedHeight for more reactivity
        const barHeight = initialHeight * 0.2 + normalizedHeight * 0.8;

        const y = (canvas.height - barHeight) / 2;

        // Create gradient from purple to cyan
        const gradient = canvasCtx.createLinearGradient(x, 0, x + barWidth, 0);
        gradient.addColorStop(0, "#8A2BE2"); // Purple
        gradient.addColorStop(1, "#00FFFF"); // Cyan

        canvasCtx.fillStyle = gradient;

        // Adjust bar width and spacing to make bars thinner and closer together
        const barActualWidth = barWidth * 0.5; // Make the bar thinner
        const barX = x + (barWidth - barActualWidth) / 2; // Center the bar in its slot

        // Draw rounded rectangle (capsule shape)
        const cornerRadius = barActualWidth / 2; // High corner radius for the capsule effect
        drawRoundedRectangle(
          canvasCtx,
          barX,
          y,
          barActualWidth,
          barHeight,
          cornerRadius,
        );

        canvasCtx.fill();
        x += barWidth;
      }

      // Continue drawing with the next animation frame
      animationFrameRef.current = requestAnimationFrame(draw);
    };

    const drawRoundedRectangle = (
      ctx: CanvasRenderingContext2D,
      x: number,
      y: number,
      width: number,
      height: number,
      radius: number,
    ) => {
      ctx.beginPath();
      ctx.moveTo(x + radius, y);
      ctx.lineTo(x + width - radius, y);
      ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
      ctx.lineTo(x + width, y + height - radius);
      ctx.quadraticCurveTo(
        x + width,
        y + height,
        x + width - radius,
        y + height,
      );
      ctx.lineTo(x + radius, y + height);
      ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
      ctx.lineTo(x, y + radius);
      ctx.quadraticCurveTo(x, y, x + radius, y);
      ctx.closePath();
    };

    draw();

    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [mediaStream, initialHeights]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        width: isMobile ? "100%" : "80%",
        height: "150px",
        borderRadius: "25px",
        marginRight: "-50%",
      }}
    />
  );
};

export default AudioWaveform;
